import { useState, useEffect, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import {
  CheckIsValidFn,
  CustomModalEditWidgetHeader,
  SaveChangesInput
} from '@Components/ModalEditWidget/modal-edit-constants';
import { awaitJSON, StreamingUtilsLambdaFetch } from '../../../utils/fetch-utils';
import { AdOpsToolProps } from '../adops-constants';

interface LinearCampaign {
  id?: number;
  company: string;
  campaign: string;
}

const HEADERS: CustomModalEditWidgetHeader[] = [
  {
    label: 'Company',
    field: 'company',
    type: 'custom',
    flex: 3,
    minFlexWidth: 150,
    modalRow: 0,
    modalWidth: 200,
    customRenderer: (data: Partial<LinearCampaign>, onChange) => (
      <Form.Control
        type='text'
        defaultValue={data.company}
        readOnly={Boolean(data.id)}
        data-testid='field-company'
        onChange={e =>
          onChange({
            ...data,
            company: e.target.value ? e.target.value.replace(/,/g, '').trim() : null
          })
        }
      />
    )
  },
  {
    label: 'Campaign',
    field: 'campaign',
    type: 'text',
    flex: 5,
    minFlexWidth: 250,
    modalRow: 0,
    modalWidth: 200
  }
];

export const LinearCampaigns = ({ utils }: AdOpsToolProps) => {
  const { setError, setModalMessage, setShowModalConfirmation } = utils;

  const [tableData, setTableData] = useState<LinearCampaign[]>();
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const res = await StreamingUtilsLambdaFetch('/linearCampaigns', {
        method: 'GET'
      });
      const data = await awaitJSON<LinearCampaign[]>(res);
      setTableData(data);
    } catch (e) {
      setError({
        message: `Failed to fetch linear campaigns. ${e.message}`,
        reportError: e
      });
    }
    setLoading(false);
  }, [setError]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const onSaveModal = useCallback(
    async ({ addedData, modifiedData, cleanup }: SaveChangesInput<LinearCampaign>) => {
      if (!addedData.length && !modifiedData.length) {
        cleanup();
        return;
      }
      try {
        await StreamingUtilsLambdaFetch('/linearCampaigns', {
          method: 'POST',
          body: JSON.stringify({
            insert: addedData,
            update: modifiedData.map(({ id, campaign }) => ({ id, campaign }))
          })
        });
        setModalMessage('Linear campaigns saved successfully');
        setShowModalConfirmation(true);
        await fetchCampaigns();
        cleanup();
      } catch (e) {
        setError({
          message: `Failed to save linear campaigns. ${e.message}`,
          reportError: e
        });
      }
    },
    [fetchCampaigns, setError, setModalMessage, setShowModalConfirmation]
  );

  const checkIsValid: CheckIsValidFn<LinearCampaign> = (data, handleInvalidMessage) => {
    if (!data.company) {
      handleInvalidMessage?.('Company is required');
      return false;
    }
    if (!data.campaign) {
      handleInvalidMessage?.('Campaign is required');
      return false;
    }
    return true;
  };

  return (
    <ModalEditWidget<LinearCampaign>
      className='linear-campaigns-table'
      name='Linear Campaigns'
      dataTestId='linear-campaigns-table'
      headers={HEADERS}
      initialTableData={tableData ?? []}
      isLoading={loading}
      onSaveModal={onSaveModal}
      selectorOptions={{}}
      rowHeight={45}
      filterBar
      enableAdd
      enableDelete={false}
      checkIsValid={checkIsValid}
      defaultNewRow={{}}
    />
  );
};
